import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from "../config/fbConfig";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import Modal from "react-modal";
import Logo from "../images/school_logo.png";
import Loader from "./Loader";
import SuccessModal from "./SuccessModal";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {SCHOOL_NAME, SCHOOL_ADDRESS, SCHOOL_PHONE } from '../Variables.js';
import '../styles/Timetable.css';

Modal.setAppElement("#root");

const Timetable = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [allTeachers, setAllTeachers] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teacherAssignments, setTeacherAssignments] = useState({});
  const [timetables, setTimetables] = useState({});
  const [currentTerm, setCurrentTerm] = useState(null);
  const [isSelectTeachersModalOpen, setIsSelectTeachersModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isSetPeriodsModalOpen, setIsSetPeriodsModalOpen] = useState(false);
  const [isEditCellModalOpen, setIsEditCellModalOpen] = useState(false);
  const [isEditAssignmentModalOpen, setIsEditAssignmentModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedPeriods, setSelectedPeriods] = useState(1);
  const [editCell, setEditCell] = useState(null);
  const [editAssignment, setEditAssignment] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
  const [teacherAllocations, setTeacherAllocations] = useState({}); // New
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [periodsPerDay, setPeriodsPerDay] = useState({
    Monday: 3,
    Tuesday: 3,
    Wednesday: 3,
    Thursday: 3,
    Friday: 3,
  });
  
  // When initializing periodConfig, ensure it has enough elements:
  const [periodConfig, setPeriodConfig] = useState(
    Array(12).fill().map((_, i) => ({
      time: `8:${i*40 % 60 === 0 ? '00' : '40'} - 8:${(i+1)*40 % 60 === 0 ? '00' : '40'}`,
      isBreak: false
    }))
  );

  const teachersCollectionRef = collection(db, "users");
  const classesCollectionRef = collection(db, "classes");
  const subjectsCollectionRef = collection(db, "subjects");
  const timetablesCollectionRef = collection(db, "timetables");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsFetchingData(true);
    try {
      const [teachersSnap, classesSnap, subjectsSnap, timetablesSnap, currentTermDoc] = await Promise.all([
        getDocs(teachersCollectionRef),
        getDocs(classesCollectionRef),
        getDocs(subjectsCollectionRef),
        getDocs(timetablesCollectionRef),
        getDoc(doc(db, 'current-term', 'current')),
      ]);
      
      // Teachers
      setAllTeachers(
        teachersSnap.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .filter((user) => user.status === "Teacher")
      );
      
      // ✅ FIXED: Better way to load classes
      const classesList = classesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setClasses(classesList);

      setSubjects(subjectsSnap.docs.map((doc) => ({ ...doc.data(), id: doc.id })));

      // ====================== CURRENT TERM ======================
      let termData = null;
      if (currentTermDoc.exists()) {
        termData = currentTermDoc.data();
        setCurrentTerm(termData);

        // Load saved period settings
        if (termData.periodsPerDay) setPeriodsPerDay(termData.periodsPerDay);
        if (termData.periodConfig) setPeriodConfig(termData.periodConfig);
      } else {
        setError("Please set the Current Term first.");
        setShowModal(true);
      }

      // ====================== TIMETABLES ======================
      const savedTimetables = {};
      const timetablesByClass = {};

      timetablesSnap.docs.forEach((doc) => {
        const classId = doc.id;
        const createdAt = doc.data().createdAt?.toDate() || new Date(0);
        
        if (!timetablesByClass[classId] || 
            (timetablesByClass[classId].createdAt < createdAt)) {
          timetablesByClass[classId] = {
            timetable: doc.data().timetable,
            createdAt,
            periodsPerDay: doc.data().periodsPerDay || periodsPerDay,
            periodConfig: doc.data().periodConfig || periodConfig
          };
        }
      });

      Object.keys(timetablesByClass).forEach(classId => {
        const timetable = timetablesByClass[classId].timetable;
        const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
        
        days.forEach(day => {
          if (!timetable[day] || !Array.isArray(timetable[day])) {
            timetable[day] = Array(periodsPerDay[day] || 5).fill('-');
          }
        });
        savedTimetables[classId] = timetable;
      });

      setTimetables(savedTimetables);

      // ====================== LOAD ALLOCATIONS ======================
      if (termData && termData.termId) {
        await loadAllocationsWithTerm(termData);
      } else {
        setTeacherAllocations({});
      }

    } catch (error) {
      setError("Failed to fetch data");
      setShowModal(true);
    } finally {
      setIsFetchingData(false);
    }
  };

  const loadAllocationsWithTerm = async (termData) => {
    if (!termData?.termId) {
      setTeacherAllocations({});
      return;
    }

    try {
      const teachersSnap = await getDocs(collection(db, "users"));
      const allocations = {};
      let count = 0;

      teachersSnap.docs.forEach((docSnap) => {
        const teacher = { id: docSnap.id, ...docSnap.data() };
        
        if (!Array.isArray(teacher.teachingAssignments)) {
          return;
        }

        const teacherAllocList = [];

        teacher.teachingAssignments.forEach((assign) => {
          if (
            assign.classId && 
            assign.subjectId && 
            assign.termId === termData.termId
          ) {
            teacherAllocList.push({
              classId: assign.classId,
              subjectId: assign.subjectId,
              periods: assign.periods || 2,
            });
          }
        });

        if (teacherAllocList.length > 0) {
          allocations[teacher.id] = teacherAllocList;
          count++;
        }
      });

      setTeacherAllocations(allocations);

    } catch (err) {
    }
  };

  // Update periods
  const updateAllocationPeriods = (teacherId, classId, subjectId, newPeriods) => {
    setTeacherAllocations(prev => {
      const updated = { ...prev };
      if (!updated[teacherId]) return prev;

      updated[teacherId] = updated[teacherId].map(item => {
        if (item.classId === classId && item.subjectId === subjectId) {
          return { ...item, periods: Math.max(1, Math.min(12, Number(newPeriods))) };
        }
        return item;
      });
      return updated;
    });
  };

  const saveAllocationPeriods = async () => {
    if (Object.keys(teacherAllocations).length === 0) return;

    setLoading(true);
    try {
      const updates = [];

      for (const teacherId of Object.keys(teacherAllocations)) {
        const teacherRef = doc(db, "users", teacherId);
        const teacher = allTeachers.find(t => t.id === teacherId);
        
        if (!teacher) continue;

        let teachingAssignments = [...(teacher.teachingAssignments || [])];

        teacherAllocations[teacherId].forEach(alloc => {
          const index = teachingAssignments.findIndex(
            a => a.classId === alloc.classId && a.subjectId === alloc.subjectId
          );

          if (index !== -1) {
            // Update existing assignment with periods
            teachingAssignments[index] = {
              ...teachingAssignments[index],
              periods: alloc.periods
            };
          } else {
            // Fallback: create new (should not happen)
            teachingAssignments.push({
              classId: alloc.classId,
              subjectId: alloc.subjectId,
              periods: alloc.periods
            });
          }
        });

        updates.push(
          updateDoc(teacherRef, { teachingAssignments })
        );
      }

      await Promise.all(updates);

      setSuccessMessage("Periods per subject saved successfully!");
      setShowModal(true);
      setIsAllocationModalOpen(false);

    } catch (err) {
      setError("Failed to save periods. Please try again.");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const savePeriodConfig = async () => {
    if (!currentTerm?.termId) {
      setError("Current term not set");
      setShowModal(true);
      return;
    }

    setLoading(true);
    try {
      const currentTermRef = doc(db, 'current-term', 'current');

      await setDoc(currentTermRef, {
        ...currentTerm,
        periodsPerDay: periodsPerDay,
        periodConfig: periodConfig,
        updatedAt: new Date()
      }, { merge: true });

      setSuccessMessage("Period configuration saved successfully!");
      setShowModal(true);
      setIsSetPeriodsModalOpen(false);

    } catch (err) {
      setError("Failed to save period configuration");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Toggle entire teacher (all subjects)
  const toggleTeacherSelection = (teacherId, checked) => {
    setTeacherAllocations(prev => {
      const updated = { ...prev };
      if (!updated[teacherId]) return prev;

      updated[teacherId] = updated[teacherId].map(alloc => ({
        ...alloc,
        checked: checked
      }));

      return updated;
    });
  };

  // Toggle single subject
  const toggleSubjectSelection = (teacherId, classId, subjectId) => {
    setTeacherAllocations(prev => {
      const updated = { ...prev };
      if (!updated[teacherId]) return prev;

      updated[teacherId] = updated[teacherId].map(alloc => {
        if (alloc.classId === classId && alloc.subjectId === subjectId) {
          return { ...alloc, checked: !alloc.checked };
        }
        return alloc;
      });

      return updated;
    });
  };

  const deleteAllTimetables = async () => {
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const timetablesSnap = await getDocs(collection(db, "timetables"));

      if (timetablesSnap.empty) {
        setSuccessMessage("No timetables found to delete.");
        setShowModal(true);
        setDeleteConfirmOpen(false);
        return;
      }

      const deletePromises = timetablesSnap.docs.map((docSnap) => 
        deleteDoc(docSnap.ref)        // ← Correct way in Firebase v9
      );

      await Promise.all(deletePromises);

      setTimetables({});
      setSuccessMessage(`Successfully deleted ${timetablesSnap.size} timetable(s).`);
      
    } catch (err) {
      setError("Failed to delete timetables.");
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setShowModal(true);
    }
  };

  const handleSelectTeachers = () => {
    setIsSelectTeachersModalOpen(true);
  };

  const confirmSelectedTeachers = () => {
    setIsSelectTeachersModalOpen(false);
  };

  const handleAssignSubject = () => {
    setError("");
    setSuccessMessage("");
    if (!selectedTeacher || !selectedSubject || !selectedClass || !selectedPeriods) {
      setError("Please select a teacher, subject, class, and number of periods");
      setShowModal(true);
      return;
    }

    setTeacherAssignments((prev) => {
      const updated = { ...prev };
      if (!updated[selectedTeacher]) updated[selectedTeacher] = [];
      if (
        !updated[selectedTeacher].some(
          (assignment) =>
            assignment.subjectId === selectedSubject &&
            assignment.classId === selectedClass
        )
      ) {
        updated[selectedTeacher].push({
          subjectId: selectedSubject,
          classId: selectedClass,
          periods: selectedPeriods,
        });
      }
      return updated;
    });
    setIsAssignModalOpen(false);
    setSelectedSubject("");
    setSelectedClass("");
    setSelectedPeriods(1);
  };

  const handleEditAssignment = (teacherId, index) => {
    const assignment = teacherAssignments[teacherId][index];
    setEditAssignment({ teacherId, index });
    setSelectedTeacher(teacherId);
    setSelectedSubject(assignment.subjectId);
    setSelectedClass(assignment.classId);
    setSelectedPeriods(assignment.periods);
    setIsEditAssignmentModalOpen(true);
  };

  const saveAssignmentEdit = () => {
    if (!selectedTeacher || !selectedSubject || !selectedClass || !selectedPeriods || !editAssignment) {
      setError("Please select a subject, class, and number of periods");
      setShowModal(true);
      return;
    }

    setTeacherAssignments((prev) => {
      const updated = { ...prev };
      updated[editAssignment.teacherId][editAssignment.index] = {
        subjectId: selectedSubject,
        classId: selectedClass,
        periods: selectedPeriods,
      };
      return updated;
    });

    setIsEditAssignmentModalOpen(false);
    setSelectedTeacher("");
    setSelectedSubject("");
    setSelectedClass("");
    setSelectedPeriods(1);
    setEditAssignment(null);
  };

  const removeAssignment = (teacherId, index) => {
    setTeacherAssignments((prev) => {
      const updated = { ...prev };
      updated[teacherId] = updated[teacherId].filter((_, i) => i !== index);
      if (updated[teacherId].length === 0) delete updated[teacherId];
      return updated;
    });
  };

  const setPeriodsForDay = (day, periods) => {
    setPeriodsPerDay((prev) => ({
      ...prev,
      [day]: Math.min(Math.max(3, periods), 12),
    }));
  };

  const handleEditCell = (classId, day, period) => {
    if (periodConfig[period].isBreak) return;
    setError("");
    setSuccessMessage("");
    setEditCell({ classId, day, period });
    setSelectedTeacher("");
    setSelectedSubject("");
    setIsEditCellModalOpen(true);
  };

  const saveCellEdit = () => {
    setError("");
    setSuccessMessage("");
    if (!selectedTeacher || !selectedSubject || !editCell) {
      setError("Please select a teacher and subject");
      setShowModal(true);
      return;
    }

    setTimetables((prev) => {
      const updated = { ...prev };
      updated[editCell.classId][editCell.day][editCell.period] = {
        teacherId: selectedTeacher,
        subjectId: selectedSubject,
      };
      return updated;
    });

    saveTimetables(timetables);
    setIsEditCellModalOpen(false);
    setSelectedTeacher("");
    setSelectedSubject("");
    setEditCell(null);
  };

  const removeCellSubject = () => {
    setError("");
    setSuccessMessage("");
    if (!editCell) {
      setError("No cell selected");
      setShowModal(true);
      return;
    }

    setTimetables((prev) => {
      const updated = { ...prev };
      updated[editCell.classId][editCell.day][editCell.period] = "-";
      return updated;
    });

    saveTimetables(timetables);
    setIsEditCellModalOpen(false);
    setSelectedTeacher("");
    setSelectedSubject("");
    setEditCell(null);
  };

  const generateTimetables = () => {
    setError("");
    setSuccessMessage("");

    if (Object.keys(teacherAllocations).length === 0) {
      setError("No teacher allocations found.");
      setShowModal(true);
      return;
    }

    const newTimetables = {};
    const teacherSchedules = {};
    const classSubjectDays = {};
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

    let totalAllocations = 0;

    // Initialize structures - only for checked allocations
    Object.keys(teacherAllocations).forEach((teacherId) => {
      teacherSchedules[teacherId] = new Set();

      teacherAllocations[teacherId].forEach((alloc) => {
        if (alloc.checked === false) return;   // Skip unchecked

        totalAllocations++;
        const { classId } = alloc;

        if (!newTimetables[classId]) {
          newTimetables[classId] = {};
          days.forEach(day => {
            newTimetables[classId][day] = Array(periodsPerDay[day] || 5).fill(null);
          });
        }

        if (!classSubjectDays[classId]) {
          classSubjectDays[classId] = {};
          days.forEach(day => {
            classSubjectDays[classId][day] = new Set();
          });
        }
      });
    });

    if (Object.keys(newTimetables).length === 0) {
      setError("No active (checked) allocations found for timetable generation.");
      setShowModal(true);
      return;
    }

    // Create all available slots
    const allSlots = [];
    Object.keys(newTimetables).forEach((classId) => {
      days.forEach((day) => {
        const numPeriods = periodsPerDay[day] || 5;
        for (let p = 0; p < numPeriods; p++) {
          if (!periodConfig[p]?.isBreak) {
            allSlots.push({ classId, day, period: p });
          }
        }
      });
    });

    // Shuffle slots
    for (let i = allSlots.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allSlots[i], allSlots[j]] = [allSlots[j], allSlots[i]];
    }

    // ==================== PLACE SUBJECTS ====================
    Object.keys(teacherAllocations).forEach((teacherId) => {
      teacherAllocations[teacherId].forEach((alloc) => {
        if (alloc.checked === false) return;     // ← THIS WAS MISSING

        let periodsToPlace = alloc.periods || 2;
        const { classId, subjectId } = alloc;

        for (const slot of allSlots) {
          if (periodsToPlace <= 0) break;

          if (
            slot.classId === classId &&
            !newTimetables[classId][slot.day][slot.period] &&
            !teacherSchedules[teacherId].has(`${slot.day}-${slot.period}`) &&
            !classSubjectDays[classId][slot.day].has(subjectId)
          ) {
            newTimetables[classId][slot.day][slot.period] = { teacherId, subjectId };
            teacherSchedules[teacherId].add(`${slot.day}-${slot.period}`);
            classSubjectDays[classId][slot.day].add(subjectId);
            periodsToPlace--;
          }
        }
      });
    });

    // Finalize timetable
    Object.keys(newTimetables).forEach((classId) => {
      days.forEach((day) => {
        newTimetables[classId][day] = newTimetables[classId][day].map((slot, index) => {
          if (slot && typeof slot === "object") return slot;
          if (periodConfig[index]?.isBreak) return "Break";
          return "-";
        });
      });
    });

    setTimetables(newTimetables);
    saveTimetables(newTimetables);
  };

  const saveTimetables = async (timetableData) => {
    setError("");
    setSuccessMessage("");
    setUpdate(true);
    try {
      await Promise.all(
        Object.keys(timetableData).map((classId) =>
          setDoc(doc(timetablesCollectionRef, classId), {
            timetable: timetableData[classId],
            periodsPerDay,
            periodConfig,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          })
        )
      );
      setSuccessMessage("Timetables saved successfully");
    } catch (error) {
      setError("Failed to save timetables");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const printTimetableByClass = () => {
    setError("");
    setSuccessMessage("");
    if (Object.keys(timetables).length === 0) {
      setError("No timetables generated yet");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape'
    });

    Object.keys(timetables).forEach((classId, index) => {
      if (index > 0) doc.addPage();

      const logoX = 14;
      const logoY = 10;
      const logoWidth = 20;
      const logoHeight = 20;

      doc.addImage(Logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${SCHOOL_NAME}`.toUpperCase(), logoX + logoWidth + 5, logoY + 9);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${SCHOOL_ADDRESS}. ${SCHOOL_PHONE}`, logoX + logoWidth + 5, logoY + 15);

      const className = classes.find(c => c.id === classId)?.name || 'Unknown Class';
      doc.setFontSize(18);
      doc.setFont('helvetica', 'normal');
      doc.text(`Timetable for ${className}`, logoX + logoWidth + 5, logoY + 24);

      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      const maxPeriods = Math.max(...Object.values(periodsPerDay));
      const head = ['Day', ...periodConfig.slice(0, maxPeriods).map(p => p.time)];
      const tableData = days.map(day => {
        const row = [day];
        timetables[classId][day].forEach((slot, periodIndex) => {
          if (periodIndex >= periodsPerDay[day]) {
            row.push('N/A');
          } else if (periodConfig[periodIndex].isBreak) {
            row.push('Break');
          } else if (slot === '-' || !slot) {
            row.push('Free');
          } else if (typeof slot === 'object') {
            const teacher = allTeachers.find(t => t.id === slot.teacherId);
            const subject = subjects.find(s => s.id === slot.subjectId);
            row.push(`${subject?.name || '?'}\n${teacher ? `(${teacher.firstName})` : '?'}`);
          } else {
            row.push(slot);
          }
        });
        return row;
      });

      autoTable(doc, {
        startY: logoY + 38,
        head: [head],
        body: tableData,
        styles: {
          fontSize: 10,
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          minCellHeight: 20,
          valign: "middle",
          halign: "center",
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: false
        },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1,
        columnStyles: {
          0: { cellWidth: 24, fontStyle: "bold" }
        }
      });
    });

    doc.save('timetables_by_class.pdf');
  };

  const printTimetableByTeacher = () => {    
    setError("");
    setSuccessMessage("");
    if (Object.keys(timetables).length === 0) {
      setError("No timetables generated yet");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape'
    });

    const teacherSchedules = {};
    const maxPeriods = Math.max(...Object.values(periodsPerDay));

    allTeachers.forEach(teacher => {
      if (selectedTeachers.includes(teacher.id)) {
        teacherSchedules[teacher.id] = {};
        ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].forEach(day => {
          teacherSchedules[teacher.id][day] = Array(maxPeriods).fill('Free');
        });
      }
    });

    Object.keys(timetables).forEach(classId => {
      const classesName = classes.find(c => c.id === classId)?.name || 'Unknown Class';
      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      
      days.forEach(day => {
        // Defensive check: Ensure timetables[classId][day] exists and is an array
        if (timetables[classId] && Array.isArray(timetables[classId][day])) {
          timetables[classId][day].forEach((slot, periodIndex) => {
            if (periodIndex < periodsPerDay[day] && slot && typeof slot === 'object' && slot.teacherId) {
              if (!teacherSchedules[slot.teacherId]) {
                teacherSchedules[slot.teacherId] = {};
                days.forEach(d => {
                  teacherSchedules[slot.teacherId][d] = Array(maxPeriods).fill('Free');
                });
              }
              const subject = subjects.find(s => s.id === slot.subjectId);
              teacherSchedules[slot.teacherId][day][periodIndex] = 
                periodConfig[periodIndex].isBreak ? 'Break' :
                `${subject?.name || '?'}\n(${classesName})`;
            }
          });
        } else {
          console.warn(`Invalid or missing timetable data for classId: ${classId}, day: ${day}`);
        }
      });
    });

    Object.keys(teacherSchedules).forEach((teacherId, index) => {
      if (index > 0) doc.addPage();

      const logoX = 14;
      const logoY = 10;
      const logoWidth = 20;
      const logoHeight = 20;

      doc.addImage(Logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${SCHOOL_NAME}`.toUpperCase(), logoX + logoWidth + 5, logoY + 9);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${SCHOOL_ADDRESS}. ${SCHOOL_PHONE}`, logoX + logoWidth + 5, logoY + 15);
      
      const teacher = allTeachers.find(t => t.id === teacherId);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'normal');
      doc.text(`Timetable for ${teacher?.firstName} ${teacher?.lastName}`, logoX + logoWidth + 5, logoY + 24);
      
      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      const head = ['Day', ...periodConfig.slice(0, maxPeriods).map(p => p.time)];
      const tableData = days.map(day => {
        const row = [day];
        const periodsToShow = periodsPerDay[day];
        for (let i = 0; i < maxPeriods; i++) {
          if (i < periodsToShow) {
            row.push(periodConfig[i].isBreak ? 'Break' : teacherSchedules[teacherId][day][i]);
          } else {
            row.push('N/A');
          }
        }
        return row;
      });

      autoTable(doc, {
        startY: logoY + 38,
        head: [head],
        body: tableData,
        styles: {
          fontSize: 10,
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          minCellHeight: 20,
          valign: "middle",
          halign: "center",
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: false
        },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1,
        columnStyles: {
          0: { cellWidth: 24, fontStyle: "bold" }
        }
      });
    });

    doc.save('timetables_by_teacher.pdf');
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <Loader text={"READING"} />;
  }

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" className="home-link" onClick={fetchData}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items column align-center scroll-fixed">
            <h2 className="manage-page-heading caps size4">Manage Timetable</h2>            
            <div className="row align-center gap-1" style={{marginBottom: '10px', width: '100%'}}>
              <button
                className="login-button"
                style={{padding: '5px', fontSize: '0.8rem'}}
                onClick={() => setIsAllocationModalOpen(true)}
              >
                Subjects
              </button>

              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem'}} 
                onClick={() => setIsSetPeriodsModalOpen(true)}
              >
                Periods per Day
              </button>

              <button
                className="login-button"
                style={{padding: '5px', fontSize: '0.8rem', backgroundColor: '#d32f2f', color: '#ffffff'}}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Delete All
              </button>
            </div>
            <div className="row align-center gap-1" style={{width: '100%'}}>              
              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem', marginTop: '0px'}} 
                onClick={generateTimetables} 
                disabled={Object.keys(teacherAllocations).length === 0}
              >
                Generate
              </button>
              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem', marginTop: '0px'}}
                onClick={printTimetableByClass}
                disabled={Object.keys(timetables).length === 0}
              >
                Print per Class
              </button>

              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem', marginTop: '0px'}}
                onClick={printTimetableByTeacher}
                disabled={Object.keys(timetables).length === 0}
              >
                Print per Teacher
              </button>
            </div>

            <div className="scroll-fixed scroll-fixed1">              
              {Object.keys(timetables).length > 0 && (
                <div>
                  <h3>Generated Timetables</h3>
                  {Object.keys(timetables).map((classId) => (
                    <div key={classId}>
                      <h4>{classes.find((c) => c.id === classId)?.name}</h4>                  
                      <div style={{ 
                        width: "100%", 
                        overflowX: "auto",
                        marginBottom: "20px"
                      }}>
                        <table style={{ 
                          tableLayout: "fixed", 
                          borderCollapse: "collapse",
                          minWidth: "100%"
                        }}>
                          <thead>
                            <tr>
                              <th style={{ 
                                width: "80px",
                                minWidth: "80px",
                                border: "1px solid #ddd", 
                                backgroundColor: "#ececec",
                                textAlign: "left",
                                position: "sticky",
                                left: 0,
                              }}>
                                Day/Time
                              </th>
                              {periodConfig.slice(0, Math.max(...Object.values(periodsPerDay))).map((period, i) => (
                                <th
                                  key={i} 
                                  style={{
                                    border: "1px solid #ddd", 
                                    textAlign: "center"
                                  }}
                                >
                                  Period {i + 1}<br/><span className="size5">{period.time}</span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((day) => (
                              <tr key={day} style={{fontSize: '0.9rem',}}>
                                <td 
                                  style={{ 
                                    width: "60px",
                                    minWidth: "60px",
                                    border: "1px solid #ddd", 
                                    backgroundColor: "#ececec",
                                    textAlign: "left",
                                    position: "sticky",
                                    fontSize: '0.8rem',
                                    left: 0,
                                  }}
                                >
                                  {day}
                                </td>
                                {timetables[classId][day].map((slot, index) => (
                                  <td
                                    key={index}
                                    style={{
                                      width: "30px",
                                      minWidth: "30px",
                                      border: "1px solid #ddd",
                                      textAlign: "center",
                                      cursor: periodConfig[index]?.isBreak ? "default" : "pointer",
                                      backgroundColor: periodConfig[index]?.isBreak ? "#f2f2f2" : "transparent"
                                    }}
                                    onClick={() => handleEditCell(classId, day, index)}
                                  >
                                    {slot && typeof slot === "object" && slot.subjectId
                                      ? subjects.find((s) => s.id === slot.subjectId)?.code || "-"
                                      : slot || "-"}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isSelectTeachersModalOpen}
        onRequestClose={() => setIsSelectTeachersModalOpen(false)}
        contentLabel="Select Teachers"
      >
        <h2>Select Teachers</h2>
        <div className="session-form">
          {allTeachers.map((teacher) => (
            <div key={teacher.id}>
              <input
                type="checkbox"
                className="gold-tick"
                id={teacher.id}
                checked={selectedTeachers.includes(teacher.id)}
                onChange={(e) => {
                  setSelectedTeachers((prev) =>
                    e.target.checked
                      ? [...prev, teacher.id]
                      : prev.filter((id) => id !== teacher.id)
                  );
                }}
              />
              <label htmlFor={teacher.id}>{`${teacher.firstName} ${teacher.lastName}`}</label>
            </div>
          ))}
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => setIsSelectTeachersModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={confirmSelectedTeachers}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isAssignModalOpen}
        onRequestClose={() => setIsAssignModalOpen(false)}
        contentLabel="Assign Subject"
      >
        <h2>Assign Subject</h2>
        <form className="session-form">
          <select
            className="input-field"
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
          >
            <option value="">Select Teacher</option>
            {selectedTeachers.map((teacherId) => {
              const teacher = allTeachers.find((t) => t.id === teacherId);
              return (
                <option key={teacherId} value={teacherId}>
                  {`${teacher.firstName} ${teacher.lastName}`}
                </option>
              );
            })}
          </select>
          <select
            className="input-field"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Select Class</option>
            {classes.map((classItem) => (
              <option key={classItem.id} value={classItem.id}>
                {classItem.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedPeriods}
            onChange={(e) => setSelectedPeriods(Number(e.target.value))}
          >
            {Array.from({ length: 6 }, (_, i) => i + 1).map((num) => (
              <option key={num} value={num}>
                {num} Period{num > 1 ? "s" : ""}
              </option>
            ))}
          </select>
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => setIsAssignModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={handleAssignSubject}
            >
              Assign
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditAssignmentModalOpen}
        onRequestClose={() => setIsEditAssignmentModalOpen(false)}
        contentLabel="Edit Assignment"
      >
        <h2>Edit Assignment</h2>
        <form className="session-form">
          <select
            className="input-field"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Select Class</option>
            {classes.map((classItem) => (
              <option key={classItem.id} value={classItem.id}>
                {classItem.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedPeriods}
            onChange={(e) => setSelectedPeriods(Number(e.target.value))}
          >
            {Array.from({ length: 6 }, (_, i) => i + 1).map((num) => (
              <option key={num} value={num}>
                {num} Period{num > 1 ? "s" : ""}
              </option>
            ))}
          </select>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsEditAssignmentModalOpen(false)}>
              Cancel
            </button>
            <button className="login-button" type="button" onClick={saveAssignmentEdit}>
              Save
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isSetPeriodsModalOpen}
        onRequestClose={() => setIsSetPeriodsModalOpen(false)}
        contentLabel="Set Periods per Day"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div style={{ maxHeight: '500px', overflowY: 'auto', marginBottom: '10px' }}>          
          <h3 className="size5 caps">
            Set Periods per Subject — {currentTerm?.sessionName} {currentTerm?.termName} Term
          </h3>

          <div className="session-form">
            <h3>Period Configurations</h3>
            {periodConfig.map((period, i) => (
              <div key={i} className="align-center" style={{marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <label>Period {i + 1} Time:</label>
                <input
                  type="text"
                  className="input-field"
                  value={period.time}
                  onChange={(e) => {
                    const newConfig = [...periodConfig];
                    newConfig[i] = { ...newConfig[i], time: e.target.value };
                    setPeriodConfig(newConfig);
                  }}
                />
                <label>
                  Break:
                  <input
                    type="checkbox"
                    className="gold-tick"
                    checked={period.isBreak}
                    onChange={(e) => {
                      const newConfig = [...periodConfig];
                      newConfig[i] = { ...newConfig[i], isBreak: e.target.checked };
                      setPeriodConfig(newConfig);
                    }}
                  />
                </label>
              </div>
            ))}
          </div>

          <div className="session-form">
            <h3>Periods per Day</h3>
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((day) => (
              <div key={day} className="session-form">
                <label>{day}</label>
                <select
                  className="input-field"
                  value={periodsPerDay[day]}
                  onChange={(e) => setPeriodsForDay(day, Number(e.target.value))}
                >
                  {Array.from({ length: 10 }, (_, i) => i + 3).map((num) => (
                    <option key={num} value={num}>
                      {num} Periods
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="row align-center gap-1" style={{ marginTop: '20px' }}>
            <button
              className="login-button"
              type="button"
              onClick={() => setIsSetPeriodsModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={savePeriodConfig}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isEditCellModalOpen}
        onRequestClose={() => setIsEditCellModalOpen(false)}
        contentLabel="Edit Timetable Cell"
      >
        <h2>Edit Timetable Cell</h2>
        <form className="session-form">
          <select
            className="input-field"
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
          >
            <option value="">Select Teacher</option>
            {allTeachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {`${teacher.firstName} ${teacher.lastName}`}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => setIsEditCellModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={saveCellEdit}
            >
              Save
            </button>            
          </div>
          <button
              className="login-button"
              type="button"
              onClick={removeCellSubject}
          >
            Remove Subject
          </button>
        </form>
      </Modal>

      {/* ==================== ALLOCATION MODAL ==================== */}    
      <Modal
        isOpen={isAllocationModalOpen}
        onRequestClose={() => setIsAllocationModalOpen(false)}
        className="custom-modal wide-modal modal-scroll-timetable"
        overlayClassName="custom-modal-overlay"
      >
        <h3 className="size5 caps">Set Periods & Select Allocations</h3>       
        <div style={{ maxHeight: '65vh', overflowY: 'auto', marginTop: '15px' }}>          
          {Object.keys(teacherAllocations).length === 0 ? (
            <p>No subjects allocated to teachers yet.</p>
          ) : (
            Object.keys(teacherAllocations).map(teacherId => {
              const teacher = allTeachers.find(t => t.id === teacherId);
              if (!teacher) return null;

              const teacherAlloc = teacherAllocations[teacherId];
              const allChecked = teacherAlloc.every(alloc => alloc.checked !== false);

              return (
                <div key={teacherId} style={{ 
                  marginBottom: '20px', 
                  padding: '15px', 
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9'
                }}>
                  {/* Teacher Level Checkbox */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={() => toggleTeacherSelection(teacherId, !allChecked)}
                      className="input-checkbox gold-tick"
                      style={{ marginRight: '10px' }}
                    />
                    <p className="bold size5" style={{ margin: 0 }}>
                      {teacher.firstName} {teacher.lastName}
                    </p>
                  </div>

                  {/* Subjects */}
                  {teacherAlloc.map((alloc, idx) => {
                    const subject = subjects.find(s => s.id === alloc.subjectId);
                    const classItem = classes.find(c => c.id === alloc.classId);

                    return (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        background: 'white',
                        borderRadius: '6px',
                        marginBottom: '8px',
                        border: '1px solid #eee'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="checkbox"
                            checked={alloc.checked !== false}
                            onChange={() => toggleSubjectSelection(teacherId, alloc.classId, alloc.subjectId)}
                            className="input-checkbox gold-tick"
                          />
                          <div>
                            <strong>{subject?.name || 'Unknown Subject'}</strong><br />
                            <small style={{ color: '#666' }}>
                              {classItem?.name || 'Unknown Class'}
                            </small>
                          </div>
                        </div>
                        <input
                          type="number"
                          min="1"
                          max="12"
                          value={alloc.periods || 2}
                          onChange={(e) => updateAllocationPeriods(teacherId, alloc.classId, alloc.subjectId, e.target.value)}
                          style={{ width: '50px', padding: '6px' }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <div className="row align-center gap-15" style={{ marginTop: '25px' }}>          
          <button onClick={() => setIsAllocationModalOpen(false)} className="login-button">
            Cancel
          </button>
          <button 
            onClick={saveAllocationPeriods} 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* ==================== DELETE CONFIRMATION MODAL ==================== */}
      <Modal
        isOpen={deleteConfirmOpen}
        onRequestClose={() => setDeleteConfirmOpen(false)}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h3 style={{ color: '#d32f2f' }}>Delete All Timetables?</h3>
        <p>This action cannot be undone. All generated timetables will be permanently deleted.</p>
        
        <div className="row align-center gap-15" style={{ marginTop: '25px' }}>
          <button onClick={() => setDeleteConfirmOpen(false)} className="login-button">
            Cancel
          </button>
          <button 
            onClick={deleteAllTimetables} 
            className="login-button"
            style={{ backgroundColor: '#d32f2f', color: 'white' }}
          >
            Yes, Delete All
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Timetable;